import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ReleaseNoteDto } from '@app/generated/model';
import { ReleaseNoteService } from '@app/core/services/release-note.service';

@Component({
  selector: 'app-edit-release-note',
  templateUrl: './edit-release-note.component.html',
  styleUrls: ['./edit-release-note.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EditReleaseNoteComponent implements OnInit, OnDestroy {
  private id: number;
  private subscriptions: Subscription;

  form: UntypedFormGroup;
  types = [
    { value: 'feature', label: 'Feature' },
    { value: 'bugfix', label: 'Bugfix' },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private releaseNoteService: ReleaseNoteService,
  ) {
    this.subscriptions = new Subscription();
    this.form = this.formBuilder.group({
      version: ['', Validators.required],
      date: ['', [Validators.required, Validators.pattern(/^\d{4}-\d{2}-\d{2}$/)]],
      type: ['', Validators.required],
      title: ['', Validators.required],
      description: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.paramMap.get('id'));
    this.loadReleaseNote();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadReleaseNote(): void {
    this.releaseNoteService
      .getAllReleaseNotes()
      .pipe(take(1))
      .subscribe({
        next: (releaseNotes) => {
          const releaseNote = releaseNotes.find((r) => r.id === this.id);
          if (releaseNote) {
            this.form.patchValue({
              version: releaseNote.version,
              date: releaseNote.date ? releaseNote.date.split('T')[0] : '',
              type: releaseNote.type,
              title: releaseNote.title,
              description: releaseNote.description,
            });
            this.form.markAsPristine();
            this.form.markAsUntouched();
          }
        },
        error: (err) => console.error('Error loading release note:', err),
      });
  }

  onClose(): void {
    this.router.navigateByUrl('/admin/release-notes');
  }

  onReset(): void {
    this.loadReleaseNote();
  }

  async onSave(): Promise<void> {
    if (!this.form.valid) {
      return;
    }
    const releaseNote = this.form.getRawValue() as ReleaseNoteDto;
    this.releaseNoteService
      .updateReleaseNote(this.id, releaseNote)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.form.markAsPristine();
          this.router.navigateByUrl('/admin/release-notes');
        },
        error: (err) => console.error('Error saving release note:', err),
      });
  }
}
